import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { setSortBy, setSortOrder } from '@/store/slices/uiSlice';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ArrowUpDown, ArrowUp, ArrowDown, Calendar, Type, Clock, FileType } from 'lucide-react';

export function SortControls() {
  const dispatch = useAppDispatch();
  const { sortBy, sortOrder } = useAppSelector((state) => state.ui.fileFilters);

  const sortOptions = [
    { value: 'name', label: 'Name', icon: Type },
    { value: 'modified', label: 'Modified', icon: Clock },
    { value: 'created', label: 'Created', icon: Calendar },
    { value: 'type', label: 'Type', icon: FileType },
  ] as const;

  const currentSort = sortOptions.find((option) => option.value === sortBy);
  const CurrentIcon = currentSort?.icon || ArrowUpDown;
  const OrderIcon = sortOrder === 'asc' ? ArrowUp : ArrowDown;

  const handleSortChange = (newSortBy: typeof sortBy) => {
    if (newSortBy === sortBy) {
      // Toggle sort order if same field
      dispatch(setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'));
    } else {
      // Change sort field
      dispatch(setSortBy(newSortBy));
      // Set default order based on field type
      dispatch(setSortOrder(newSortBy === 'name' || newSortBy === 'type' ? 'asc' : 'desc'));
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-2">
          <CurrentIcon className="h-3 w-3" />
          <OrderIcon className="h-3 w-3" />
          <span className="hidden md:inline">{currentSort?.label}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {sortOptions.map((option) => {
          const Icon = option.icon;
          const isActive = sortBy === option.value;
          return (
            <DropdownMenuItem
              key={option.value}
              onClick={() => handleSortChange(option.value)}
              className="flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <Icon className="h-4 w-4" />
                <span>{option.label}</span>
              </div>
              {isActive && (
                <div className="flex items-center gap-1">
                  {sortOrder === 'asc' ? (
                    <ArrowUp className="h-3 w-3" />
                  ) : (
                    <ArrowDown className="h-3 w-3" />
                  )}
                </div>
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
